import { toast } from '@blocksuite/affine-components/toast';
import { insertLinkedNode } from '@blocksuite/affine-inline-reference';
import type {
  ListBlockModel,
  ParagraphBlockModel,
} from '@blocksuite/affine-model';
import {
  getInlineEditorByModel,
  insertContent,
} from '@blocksuite/affine-rich-text';
import { JournalProvider } from '@blocksuite/affine-shared/services';
import {
  ArrowDownBigIcon,
  ArrowUpBigIcon,
  CopyIcon,
  DateTimeIcon,
  DeleteIcon,
  DualLinkIcon,
  NowIcon,
  TodayIcon,
  TomorrowIcon,
  YesterdayIcon,
} from '@blocksuite/icons/lit';
import { type DeltaInsert, Slice, Text } from '@blocksuite/store';
import * as Y from 'yjs';

import {
  formatRelativeDateOffset,
  getNextDateCandidates,
  getRelativeDateOffsetCandidates,
  isValidDate,
  NEXT_DATE_TARGETS,
  parseNextDateTarget,
  parseRelativeDateOffset,
  type RelativeDateOffset,
  resolveNextDate,
  resolveRelativeDateOffset,
} from './relative-date';
import { slashMenuToolTips } from './tooltips';
import type {
  SlashMenuActionContext,
  SlashMenuActionItem,
  SlashMenuConfig,
  SlashMenuContext,
} from './types';
import {
  addDays,
  addHours,
  addMinutes,
  formatDate,
  formatTime,
  formatTimeOfDay,
} from './utils';

const relativeDateShortcuts = [
  { amount: 2, unit: 'days' },
  { amount: 3, unit: 'days' },
  { amount: 2, unit: 'weeks' },
] satisfies RelativeDateOffset[];

const inDatePresets = [
  { amount: 2, unit: 'days' },
  { amount: 3, unit: 'days' },
  { amount: 7, unit: 'days' },
  { amount: 10, unit: 'days' },
  { amount: 2, unit: 'weeks' },
  { amount: 1, unit: 'months' },
] satisfies RelativeDateOffset[];

const IN_DATE_HINT = 'Try /in 13 days, /in 4wk, /in a month, or /in one year';

const NEXT_DATE_HINT =
  'Try /next monday, /next fri, /next week, or /next month';

const relativeTimeShortcuts = [
  { name: 'In 15 mins', minutes: 15, aliases: ['15 mins'] },
  { name: 'In 30 mins', minutes: 30, aliases: ['30 mins'] },
  { name: 'In 45 mins', minutes: 45, aliases: ['45 mins'] },
  { name: 'In 1 hour', hours: 1, aliases: ['1hr', 'in 1hr'] },
  { name: 'In 2 hours', hours: 2, aliases: ['2hrs', 'in 2hrs'] },
  { name: 'In 3 hours', hours: 3, aliases: ['3hrs', 'in 3hrs'] },
  { name: 'In 6 hours', hours: 6, aliases: ['6hrs', 'in 6hrs'] },
];

function normalizeSearchText(text: string) {
  return text.trim().toLocaleLowerCase().replace(/\s+/g, ' ');
}

function withoutInPrefix(text: string) {
  return normalizeSearchText(text).replace(/^in\s+/, '');
}

function createInDateItem(
  offset: RelativeDateOffset,
  now: Date
): SlashMenuActionItem | null {
  const date = resolveRelativeDateOffset(now, offset);
  if (!isValidDate(date)) return null;

  const duration = formatRelativeDateOffset(offset);
  return {
    name: `In ${duration}`,
    icon: DateTimeIcon(),
    description: formatDate(date),
    action: (ctx: SlashMenuContext) => {
      const freshDate = resolveRelativeDateOffset(new Date(), offset);
      if (!isValidDate(freshDate)) {
        toast(ctx.std.host, IN_DATE_HINT);
        return;
      }
      insertJournalDateLink(ctx, freshDate);
    },
  };
}

function createNextDateItem(
  target: (typeof NEXT_DATE_TARGETS)[number],
  now: Date
): SlashMenuActionItem {
  return {
    name: target.name,
    icon: DateTimeIcon(),
    description: formatDate(resolveNextDate(now, target)),
    searchAlias: target.aliases,
    action: (ctx: SlashMenuContext) => {
      insertJournalDateLink(ctx, resolveNextDate(new Date(), target));
    },
  };
}

function findRelativeTimeArgumentItems(argument: string, now: Date) {
  const query = normalizeSearchText(argument);
  if (!query) return [];

  const items = createRelativeTimeItems(now);
  const entries = relativeTimeShortcuts.map((shortcut, index) => ({
    item: items[index],
    candidates: [
      withoutInPrefix(shortcut.name),
      ...shortcut.aliases.map(withoutInPrefix),
    ],
  }));

  const exact = entries.filter(({ candidates }) => candidates.includes(query));
  const matched =
    exact.length > 0
      ? exact
      : entries.filter(({ candidates }) =>
          candidates.some(candidate => candidate.startsWith(query))
        );

  return matched.map(({ item }) => item);
}

function getInDateArgumentItems(
  actionContext: SlashMenuActionContext,
  now: Date
) {
  const argument = actionContext.argument.trim();
  const offsets = argument
    ? getRelativeDateOffsetCandidates(argument)
    : inDatePresets;
  const dateItems = offsets
    .map(offset => createInDateItem(offset, now))
    .filter((item): item is SlashMenuActionItem => item !== null);

  return argument
    ? [...dateItems, ...findRelativeTimeArgumentItems(argument, now)]
    : dateItems;
}

function getNextDateArgumentItems(
  actionContext: SlashMenuActionContext,
  now: Date
) {
  return getNextDateCandidates(actionContext.argument).map(target =>
    createNextDateItem(target, now)
  );
}

function applyInDate(
  ctx: SlashMenuContext,
  actionContext?: SlashMenuActionContext
) {
  const offset = parseRelativeDateOffset(actionContext?.argument ?? '');
  if (!offset) {
    toast(ctx.std.host, IN_DATE_HINT);
    return;
  }

  const date = resolveRelativeDateOffset(new Date(), offset);
  if (!isValidDate(date)) {
    toast(ctx.std.host, IN_DATE_HINT);
    return;
  }
  insertJournalDateLink(ctx, date);
}

function applyNextDate(
  ctx: SlashMenuContext,
  actionContext?: SlashMenuActionContext
) {
  const target = parseNextDateTarget(actionContext?.argument ?? '');
  if (!target) {
    toast(ctx.std.host, NEXT_DATE_HINT);
    return;
  }
  insertJournalDateLink(ctx, resolveNextDate(new Date(), target));
}

function getRelativeDate(offset: number) {
  return addDays(new Date(), offset);
}

function getDateSelectorPosition({ std, model }: SlashMenuContext) {
  const inlineEditor = getInlineEditorByModel(std, model);
  if (!inlineEditor?.rootElement) return undefined;

  let rect = inlineEditor.getNativeRange()?.getBoundingClientRect();
  if (!rect || rect.width === 0 || rect.height === 0) {
    rect = inlineEditor.rootElement.getBoundingClientRect();
  }

  return [rect.x, rect.y, rect.width, rect.height || 20] as [
    number,
    number,
    number,
    number,
  ];
}

function insertJournalDateLink(ctx: SlashMenuContext, date: Date) {
  const dateString = formatDate(date);
  const journalProvider = ctx.std.getOptional(JournalProvider);
  const inlineEditor = getInlineEditorByModel(ctx.std, ctx.model);

  if (!journalProvider || !inlineEditor) {
    insertContent(ctx.std, ctx.model, dateString);
    return;
  }

  insertLinkedNode({
    inlineEditor,
    docId: journalProvider.ensureJournalDoc(dateString),
  });
}

function createRelativeDateItems(now: Date): SlashMenuActionItem[] {
  const dateItems = relativeDateShortcuts
    .map(offset => createInDateItem(offset, now))
    .filter((item): item is SlashMenuActionItem => item !== null);
  const nextItems = NEXT_DATE_TARGETS.map(target =>
    createNextDateItem(target, now)
  );

  return [
    ...dateItems.slice(0, 2),
    nextItems[0],
    ...dateItems.slice(2),
    ...nextItems.slice(1),
  ];
}

function createRelativeTimeItems(now: Date): SlashMenuActionItem[] {
  return relativeTimeShortcuts.map(({ name, minutes, hours, aliases }) => {
    const getTime = (date: Date) =>
      typeof minutes === 'number'
        ? addMinutes(date, minutes)
        : addHours(date, hours);

    return {
      name,
      icon: DateTimeIcon(),
      description: formatTimeOfDay(getTime(now)),
      searchAlias: aliases,
      action: ({ std, model }: SlashMenuContext) => {
        insertContent(std, model, formatTimeOfDay(getTime(new Date())));
      },
    };
  });
}

export const defaultSlashMenuConfig: SlashMenuConfig = {
  items: ctx => {
    const now = new Date();
    const tomorrow = getRelativeDate(1);
    const yesterday = getRelativeDate(-1);
    const journalProvider = ctx.std.getOptional(JournalProvider);
    const datePickerItem = journalProvider?.selectDate
      ? [
          {
            name: 'Date picker',
            icon: DateTimeIcon(),
            description: 'Select a journal date',
            group: '6_Date@9' as const,
            action: (ctx: SlashMenuContext) => {
              const journalProvider = ctx.std.getOptional(JournalProvider);
              const inlineEditor = getInlineEditorByModel(ctx.std, ctx.model);
              if (!journalProvider?.selectDate) return;
              // Opening the mobile calendar clears the editor selection.
              // Keep the position left after the slash query was removed.
              const inlineRange = inlineEditor?.getInlineRange();
              const yText = inlineEditor?.yText;
              const position =
                inlineRange && yText
                  ? Y.createRelativePositionFromTypeIndex(
                      yText,
                      inlineRange.index
                    )
                  : undefined;
              journalProvider
                .selectDate(getDateSelectorPosition(ctx))
                .then(async dateString => {
                  if (!inlineEditor) {
                    if (dateString)
                      insertContent(ctx.std, ctx.model, dateString);
                    return;
                  }
                  // Let React unmount the calendar's focus scope before
                  // returning focus to the editor (including on cancellation).
                  await new Promise<void>(resolve =>
                    requestAnimationFrame(() => resolve())
                  );
                  await inlineEditor.waitForUpdate();
                  const root = inlineEditor.rootElement;
                  if (!root?.isConnected || !position || !yText?.doc) {
                    return;
                  }
                  const absolute = Y.createAbsolutePositionFromRelativePosition(
                    position,
                    yText.doc
                  );
                  if (!absolute || absolute.type !== yText) return;
                  inlineEditor.eventSource?.focus({ preventScroll: true });
                  ctx.std.event.active = true;
                  const range = {
                    index: absolute.index,
                    length: 0,
                  };
                  // Android restores its previous DOM caret on focus. Sync it
                  // before the provider reads the range for linked insertion.
                  inlineEditor.syncInlineRange(range);
                  inlineEditor.setInlineRange(range);
                  if (!dateString) return;
                  insertLinkedNode({
                    inlineEditor,
                    docId: journalProvider.ensureJournalDoc(dateString),
                  });
                })
                .catch(console.error);
            },
          },
        ]
      : [];

    return [
      {
        name: 'Today',
        icon: TodayIcon(),
        tooltip: slashMenuToolTips['Today'],
        description: formatDate(now),
        group: '6_Date@0',
        action: ctx => {
          insertJournalDateLink(ctx, new Date());
        },
      },
      {
        name: 'Tomorrow',
        icon: TomorrowIcon(),
        tooltip: slashMenuToolTips['Tomorrow'],
        description: formatDate(tomorrow),
        group: '6_Date@1',
        action: ctx => {
          insertJournalDateLink(ctx, getRelativeDate(1));
        },
      },
      {
        name: 'Yesterday',
        icon: YesterdayIcon(),
        tooltip: slashMenuToolTips['Yesterday'],
        description: formatDate(yesterday),
        group: '6_Date@2',
        action: ctx => {
          insertJournalDateLink(ctx, getRelativeDate(-1));
        },
      },
      {
        name: 'Now',
        icon: NowIcon(),
        tooltip: slashMenuToolTips['Now'],
        description: formatTime(now),
        group: '6_Date@3',
        action: ({ std, model }) => {
          insertContent(std, model, formatTime(now));
        },
      },
      {
        name: 'Time',
        icon: DateTimeIcon(),
        description: formatTimeOfDay(now),
        searchAlias: ['current time'],
        group: '6_Date@4',
        action: ({ std, model }) => {
          insertContent(std, model, formatTimeOfDay(new Date()));
        },
      },
      {
        name: 'Next',
        icon: DateTimeIcon(),
        description: 'Next weekday, week, or month',
        group: '6_Date@5',
        acceptsArgument: true,
        action: applyNextDate,
        getArgumentItems: (_ctx, actionContext) =>
          getNextDateArgumentItems(actionContext, now),
      },
      {
        name: 'In',
        icon: DateTimeIcon(),
        description: 'Insert a date relative to today',
        group: '6_Date@6',
        acceptsArgument: true,
        action: applyInDate,
        getArgumentItems: (_ctx, actionContext) =>
          getInDateArgumentItems(actionContext, now),
      },
      {
        name: 'Relative dates',
        icon: DateTimeIcon(),
        description: 'Pick a future journal date',
        group: '6_Date@7',
        subMenu: createRelativeDateItems(now),
      },
      {
        name: 'Relative times',
        icon: DateTimeIcon(),
        description: 'Insert a future time',
        group: '6_Date@8',
        subMenu: createRelativeTimeItems(now),
      },
      ...datePickerItem,
      {
        name: 'Move Up',
        description: 'Shift this line up.',
        icon: ArrowUpBigIcon(),
        tooltip: slashMenuToolTips['Move Up'],
        group: '8_Actions@0',
        action: ({ std, model }) => {
          const { host } = std;
          const previousSiblingModel = host.store.getPrev(model);
          if (!previousSiblingModel) return;

          const parentModel = host.store.getParent(previousSiblingModel);
          if (!parentModel) return;

          host.store.moveBlocks(
            [model],
            parentModel,
            previousSiblingModel,
            true
          );
        },
      },
      {
        name: 'Move Down',
        description: 'Shift this line down.',
        icon: ArrowDownBigIcon(),
        tooltip: slashMenuToolTips['Move Down'],
        group: '8_Actions@1',
        action: ({ std, model }) => {
          const { host } = std;
          const nextSiblingModel = host.store.getNext(model);
          if (!nextSiblingModel) return;

          const parentModel = host.store.getParent(nextSiblingModel);
          if (!parentModel) return;

          host.store.moveBlocks([model], parentModel, nextSiblingModel, false);
        },
      },
      {
        name: 'Copy',
        description: 'Copy this line to clipboard.',
        icon: CopyIcon(),
        tooltip: slashMenuToolTips['Copy'],
        group: '8_Actions@2',
        action: ({ std, model }) => {
          const slice = Slice.fromModels(std.store, [model]);

          std.clipboard
            .copy(slice)
            .then(() => {
              toast(std.host, 'Copied to clipboard');
            })
            .catch(e => {
              console.error(e);
            });
        },
      },
      {
        name: 'Duplicate',
        description: 'Create a duplicate of this line.',
        icon: DualLinkIcon(),
        tooltip: slashMenuToolTips['Copy'],
        group: '8_Actions@3',
        action: ({ std, model }) => {
          if (!model.text || !(model.text instanceof Text)) {
            console.error("Can't duplicate a block without text");
            return;
          }
          const { host } = std;
          const parent = host.store.getParent(model);
          if (!parent) {
            console.error(
              'Failed to duplicate block! Parent not found: ' +
                model.id +
                '|' +
                model.flavour
            );
            return;
          }
          const index = parent.children.indexOf(model);

          // FIXME: this clone is not correct
          host.store.addBlock(
            model.flavour,
            {
              type: (model as ParagraphBlockModel).props.type,
              text: new Text(
                (
                  model as ParagraphBlockModel
                ).props.text.toDelta() as DeltaInsert[]
              ),
              checked: (model as ListBlockModel).props.checked,
            },
            host.store.getParent(model),
            index
          );
        },
      },
      {
        name: 'Delete',
        description: 'Remove this line permanently.',
        searchAlias: ['remove'],
        icon: DeleteIcon(),
        tooltip: slashMenuToolTips['Delete'],
        group: '8_Actions@4',
        action: ({ std, model }) => {
          std.host.store.deleteBlock(model);
        },
      },
    ];
  },
};
